import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, TriangleAlert } from "lucide-react";
import type { Analytics, AnalyticsKpis, AnalyticsPeriod } from "../../api";
import { buildInsights, type InsightSeverity } from "./insights";

const ICONS: Record<InsightSeverity, typeof Info> = {
  danger: AlertTriangle,
  warning: TriangleAlert,
  info: Info,
};

const TONES: Record<InsightSeverity, string> = {
  danger: "border-chart-negative/25 bg-chart-negative/10 text-chart-negative",
  warning: "border-warning/25 bg-warning/10 text-warning",
  info: "border-border bg-muted/60 text-foreground",
};

/**
 * "E'tibor berish kerak" — determinik qoidalardan chiqqan 1–3 ta chip.
 *
 * Qoida ishlamasa BUTUN blok chizilmaydi: tinch davrda "hammasi joyida"
 * kartasi turishi bu joyni bezakka aylantirardi va vaqt o'tib ega uni umuman
 * ko'rmay qo'yardi.
 */
export function InsightStrip({
  kpis,
  analytics,
  hasProducts,
  period,
}: {
  kpis: AnalyticsKpis | null;
  analytics: Analytics | null;
  hasProducts: boolean;
  period: AnalyticsPeriod;
}) {
  const { t } = useTranslation("analytics");
  const insights = useMemo(
    () => buildInsights({ kpis, analytics, hasProducts, period }),
    [kpis, analytics, hasProducts, period],
  );

  if (insights.length === 0) return null;

  return (
    <section aria-labelledby="insights-heading" className="space-y-2">
      <h3
        id="insights-heading"
        className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground"
      >
        {t("insights.title")}
      </h3>
      <ul className="flex flex-wrap gap-2">
        {insights.map((ins) => {
          const Icon = ICONS[ins.severity];
          return (
            <li
              key={ins.id}
              className={
                "inline-flex items-start gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium " +
                TONES[ins.severity]
              }
              // Ta'rifi noaniq o'lchovlarda izoh native `title`da: chip ichiga
              // `InfoTooltip` qo'yib bo'lmaydi (u <button>).
              title={ins.hintKey ? t(`kpi.hints.${ins.hintKey}`) : undefined}
            >
              <Icon className="size-3.5 shrink-0 mt-px" />
              <span>{t(`insights.${ins.messageKey}`, ins.params)}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
